import { useEffect, useRef, useState } from "react";
import type { EstudoAtual, ItemEmExecucao } from "@noesis/shared";
import { pedir } from "../api.js";
import { NaoEntendi } from "./NaoEntendi.js";
import { Icone } from "../componentes/Icone.js";
import { IconeCaderno, IconeDuvida, IconeIa, IconeMarcado } from "../componentes/icones.js";

/**
 * A barra de ações da execução — RN-52 a RN-55.
 *
 * **Três alvos, só de ícone, à direita do topo**: marcar, "não entendi" e
 * encerrar. À esquerda ficam o progresso, o relógio da sessão e a matéria e
 * assunto do item, e embaixo a barra de progresso. Nada mais.
 *
 * Eram cinco botões com texto em duas linhas, e é o defeito que esta versão
 * conserta: **cinco alvos rotulados no topo da tela de execução são distração
 * no lugar onde a atenção deveria estar no enunciado**. A questão é o conteúdo
 * da tela; a barra é ferramenta, e ferramenta não compete com o trabalho.
 * ADR 020.
 *
 * O rótulo saiu da tela, **não da árvore**: `aria-label` em todo botão, mais
 * `title` para a dica no apontamento. É o mesmo trato do estado recolhido da
 * barra lateral — quem depende do leitor de tela não perde nada.
 *
 * **Anotar saiu daqui.** Ele e "caderno" eram duas portas para a mesma coisa na
 * mesma barra: escrever sobre o que está na tela. Ficou o caderno, e a anotação
 * do item passou para dentro do painel dele (RN-42). **O caderno, por sua vez,
 * saiu da barra** e virou um alvo do menu flutuante do rodapé (ADR 028),
 * montado por quem monta a tela de execução.
 *
 * **O rótulo voltou para a tela no largo** (ADR 041): marcar, caderno, não
 * entendi e assistente, escritos embaixo do ícone. Quem abre o app pela
 * primeira vez não sabe o que é um marcador de livro nem um brilho, e o tour
 * aponta para esta barra justamente para dizer. O assistente veio do canto
 * flutuante para cá no largo; no celular os alvos seguem só de ícone e o
 * assistente continua no canto, onde o polegar alcança.
 *
 * **Marcar é ícone de dois estados**, aceso ou apagado. O
 * estado não depende só de cor: muda o **anel de borda** e muda o **peso do
 * glifo**, de contorno para área cheia (papel `estado-ligado` em `Icone.tsx`).
 *
 * **Um relógio só, o da sessão** (RN-53). O cronômetro por item continua sendo
 * gravado em `resposta.tempoMs` e alimenta o tempo médio das estatísticas, mas
 * não aparece aqui: dois relógios na mesma tela competem, e o do item
 * transforma cada questão em contra-relógio. No simulado é o oposto, e de
 * propósito — lá o contra-relógio é a prova (RN-55).
 *
 * **No simulado a barra muda** (RN-55): "não entendi" fica indisponível, marcar
 * continua, e encerrar significa entregar. Ele fica **desabilitado e visível**,
 * não escondido: sumir move os alvos restantes de lugar entre uma tela e outra,
 * e a mão que já aprendeu onde fica "marcar" erraria na prova, que é onde errar
 * custa mais. Vale igual para os dois alvos do menu do rodapé.
 */

export type ModoDaExecucao = "estudo" | "simulado";

/**
 * A que grau uma dificuldade escrita à mão pertence.
 *
 * O campo é **texto livre** por decisão do formato de importação: quem importa
 * decide a escala ("Fácil", "3", "TRF"). Então o chip não pode ter uma cor por
 * valor — ele reconhece as três palavras que o acervo de fato usa e cai no
 * neutro em qualquer outro caso. Neutro não é falha: é o que a maioria das
 * escalas merece.
 */
export function grauDaDificuldade(valor: string): "facil" | "media" | "dificil" {
  const limpo = valor.trim().toLocaleLowerCase("pt-BR");
  if (limpo.startsWith("fác") || limpo.startsWith("fac")) return "facil";
  if (limpo.startsWith("dif")) return "dificil";
  return "media";
}

export function BarraDeAcoes({
  atual,
  item,
  modo,
  aoEncerrar,
  prazoEm,
  aoAlternarCaderno,
  cadernoAberto,
  aoAbrirAssistente,
}: {
  atual: EstudoAtual;
  item: ItemEmExecucao;
  modo: ModoDaExecucao;
  aoEncerrar: () => void;
  /** Só no simulado: o instante em que o tempo acaba, calculado pelo servidor. */
  prazoEm?: string;
  /** Abre e fecha o painel do caderno. Ausente no simulado (RN-55). */
  aoAlternarCaderno?: () => void;
  /** O painel está aberto agora — o alvo é de dois estados. */
  cadernoAberto?: boolean;
  /** Abre o painel do assistente. Ausente no simulado (RN-55). */
  aoAbrirAssistente?: () => void;
}) {
  const p = atual.sessao?.progresso;
  const feitos = p ? p.cursor : 0;
  const total = p ? p.total : 0;

  const [marcado, setMarcado] = useState(item.marcado);
  const [naoEntendi, setNaoEntendi] = useState(false);
  const [confirmandoFim, setConfirmandoFim] = useState(false);

  useEffect(() => {
    setMarcado(item.marcado);
    setNaoEntendi(false);
  }, [item.id, item.marcado]);

  const soDoEstudo = modo === "estudo";

  return (
    <header className="sessao__topo">
      <div className="sessao__barra">
        <button
          type="button"
          className="botao-icone sessao__sair"
          aria-label={soDoEstudo ? "Encerrar a sessão" : "Entregar a prova"}
          title={soDoEstudo ? "Encerrar a sessão" : "Entregar a prova"}
          onClick={() => setConfirmandoFim(true)}
        >
          <Icone glifo="x" papel="acao" />
        </button>

        <div className="sessao__progresso">
          <p className="t-anotacao sessao__contagem">
            questão {Math.min(feitos + 1, total)} de {total}
          </p>
          <Tiques atual={Math.min(feitos + 1, total)} total={total} />
        </div>

        <Relogio inicio={atual.sessao?.iniciadaEm ?? null} prazoEm={prazoEm} />

        {!soDoEstudo && (
          <button type="button" className="botao botao--secundario sessao__entregar" onClick={() => setConfirmandoFim(true)}>
            <Icone glifo="enviar" papel="acao" />
            Entregar prova
          </button>
        )}

        <div className="sessao__ferramentas" role="toolbar" aria-label="Ações da questão" data-tour="icones">
          <Acao
            legenda="marcar"
            rotulo={marcado ? "Desmarcar o item" : "Marcar o item"}
            glifo={IconeMarcado}
            ativo={marcado}
            aoTocar={() => {
              setMarcado((v) => !v);
              void pedir("item.marcar", { params: { id: item.id } })
                .then((r) => setMarcado(r.marcado))
                .catch(() => setMarcado(item.marcado));
            }}
          />
          <Acao
            legenda="caderno"
            marcaDoAlvo="caderno"
            rotulo={cadernoAberto ? "Fechar o caderno" : "Abrir o caderno"}
            atalho="N"
            glifo={IconeCaderno}
            ativo={cadernoAberto ?? false}
            indisponivel={!soDoEstudo || !aoAlternarCaderno}
            motivo="O caderno fica indisponível no simulado."
            aoTocar={() => aoAlternarCaderno?.()}
          />
          <Acao
            legenda="não entendi"
            rotulo="Registrar que não entendi"
            glifo={IconeDuvida}
            ativo={naoEntendi}
            indisponivel={!soDoEstudo}
            motivo="Registrar dúvida fica indisponível no simulado."
            aoTocar={() => setNaoEntendi((v) => !v)}
          />
          <Acao
            legenda="assistente"
            marcaDoAlvo="assistente"
            rotulo="Perguntar ao assistente sobre esta questão"
            glifo={IconeIa}
            indisponivel={!soDoEstudo || !aoAbrirAssistente}
            motivo="O assistente fica indisponível no simulado."
            aoTocar={() => aoAbrirAssistente?.()}
          />
        </div>

        {/* Abre embaixo do botão que o chamou, por cima da questão, e não no
            meio dela: é um pedido sobre a questão, não parte da resposta. */}
        {naoEntendi && soDoEstudo && (
          <div className="sessao__duvida" onKeyDown={(e) => e.key === "Escape" && setNaoEntendi(false)}>
            <NaoEntendi item={item} aoFechar={() => setNaoEntendi(false)} />
          </div>
        )}
      </div>

      <p className="sessao__onde">
        {item.categoria.length > 0 && <span className="t-anotacao t-2">{item.categoria.join(", ")}</span>}
        {item.dificuldade && (
          <span className={`etiqueta-dif etiqueta-dif--${grauDaDificuldade(item.dificuldade)}`}>{item.dificuldade}</span>
        )}
        {item.tipo !== "multipla" && <span className="t-anotacao-p">· {NOME_DO_TIPO_MINUSCULO[item.tipo]}</span>}
      </p>

      {confirmandoFim && (
        <ConfirmarFim
          modo={modo}
          aoCancelar={() => setConfirmandoFim(false)}
          aoConfirmar={() => {
            setConfirmandoFim(false);
            aoEncerrar();
          }}
        />
      )}
    </header>
  );
}

const NOME_DO_TIPO_MINUSCULO: Record<ItemEmExecucao["tipo"], string> = {
  multipla: "múltipla",
  certo_errado: "certo ou errado",
  cartao: "cartão",
  cloze: "lacunas",
};

/**
 * Os tiques de progresso do Caderno: um traço por questão, azul até a atual
 * e bege no resto. É a barra de progresso da sessão com cara de caderno, e
 * continua sendo `progressbar` para o leitor de tela.
 */
export function Tiques({ atual, total }: { atual: number; total: number }) {
  const n = Math.max(total, 1);
  const passo = total > 30 ? 6 : 13;
  const largura = (n - 1) * passo + 6;
  return (
    <svg
      className="tiques"
      width={largura}
      height="24"
      viewBox={`-3 0 ${largura} 24`}
      role="progressbar"
      aria-label="Progresso da sessão"
      aria-valuenow={atual}
      aria-valuemin={0}
      aria-valuemax={total}
    >
      {Array.from({ length: n }, (_, i) => (
        <path
          key={i}
          className={i < atual ? "tiques__feito" : undefined}
          d={`M${i * passo} ${2 + (i % 3)} L${i * passo + (i % 2 ? 0.8 : -0.6)} 22`}
        />
      ))}
    </svg>
  );
}

function Relogio({ inicio, prazoEm }: { inicio: string | null; prazoEm?: string }) {
  const [agora, setAgora] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (prazoEm) {
    const restam = Math.max(Math.floor((Date.parse(prazoEm) - agora) / 1000), 0);
    return (
      <p className={restam <= 60 ? "sessao__relogio sessao__relogio--prova sessao__relogio--fim" : "sessao__relogio sessao__relogio--prova"}>
        <Icone glifo="simulado" papel="navegacao" />
        <span className="so-leitor">Restam </span>
        {comoRelogio(restam, true)}
      </p>
    );
  }

  if (!inicio) return null;
  const decorrido = Math.max(Math.floor((agora - Date.parse(inicio)) / 1000), 0);
  return (
    <p className="t-anotacao sessao__relogio">
      <span className="so-leitor">Tempo de sessão </span>
      {comoRelogio(decorrido)}
    </p>
  );
}

function comoRelogio(segundos: number, comHoras = false): string {
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;
  const doisDigitos = (n: number) => String(n).padStart(2, "0");
  return h > 0 || comHoras ? `${h}:${doisDigitos(m)}:${doisDigitos(s)}` : `${m}:${doisDigitos(s)}`;
}

/* ------------------------------------------------------------------ alvos */

/**
 * Um alvo da barra: **só ícone, nunca texto pintado** (RN-52).
 *
 * O nome existe em dois lugares que não dependem de largura: `aria-label`, que
 * é o nome acessível do controle, e `title`, que é a dica no apontamento. O que
 * saiu foi o texto desenhado na tela.
 *
 * `ativo` é o segundo estado dos botões que alternam. Ele não se anuncia só por
 * cor: o `aria-pressed` liga um **anel de borda** na folha, e o papel do ícone
 * passa de `estado` para `estado-ligado`, que troca contorno por área cheia.
 * Duas dimensões, e nenhuma delas é matiz.
 */
function Acao({
  legenda,
  rotulo,
  atalho,
  marcaDoAlvo,
  glifo,
  ativo,
  indisponivel,
  motivo,
  aoTocar,
}: {
  /** A palavra desenhada embaixo do ícone no largo (ADR 041). */
  legenda: string;
  rotulo: string;
  /** Um `data-alvo` para a execução reencontrar este botão ao devolver o foco. */
  marcaDoAlvo?: string;
  /** O atalho de teclado, quando há: entra na dica junto com o nome (Fase 1F). */
  atalho?: string;
  glifo: Parameters<typeof Icone>[0]["glifo"];
  /** `undefined` num botão que não alterna: `aria-pressed` num alvo de ação só
      diria "não pressionado" para sempre. */
  ativo?: boolean;
  indisponivel?: boolean;
  motivo?: string;
  aoTocar: () => void;
}) {
  return (
    <button
      type="button"
      className="botao-icone sessao__acao"
      data-alvo={marcaDoAlvo}
      // Alvo de 44px e nome acessível em cada botão só de ícone (RN-52).
      aria-label={indisponivel ? `${rotulo} — ${motivo ?? "indisponível aqui"}` : rotulo}
      aria-pressed={ativo ?? undefined}
      disabled={indisponivel}
      title={indisponivel ? motivo : atalho ? `${rotulo} · ${atalho}` : rotulo}
      onClick={aoTocar}
    >
      <Icone glifo={glifo} papel={ativo ? "estado-ligado" : "acao"} />
      <span className="sessao__acao-legenda" aria-hidden="true">
        {legenda}
      </span>
    </button>
  );
}

/* ------------------------------------------------------- confirmar o fim */

/**
 * Encerrar exige confirmação (RN-54).
 *
 * **Não é medo de perder resposta** — resposta é evento imutável, já persistido
 * (RN-28). É que a sessão é retomável por cursor no servidor (RN-10): sair da
 * tela preserva a retomada, encerrar a descarta. Quem toca "Encerrar" achando
 * que está saindo perde a fila montada, não o histórico.
 *
 * A confirmação é simples, sem digitar nome: a RN-25 cobre exclusão de dado, e
 * isto não é exclusão de dado.
 */
function ConfirmarFim({
  modo,
  aoConfirmar,
  aoCancelar,
}: {
  modo: ModoDaExecucao;
  aoConfirmar: () => void;
  aoCancelar: () => void;
}) {
  const dialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialogo.current;
    if (d && !d.open) d.showModal();
  }, []);

  return (
    <dialog className="folha-dialogo" ref={dialogo} aria-labelledby="fim-titulo" onClose={aoCancelar}>
      <div className="confirmar-fim">
        <h2 className="t-cartao" id="fim-titulo">
          {modo === "estudo" ? "Encerrar a sessão?" : "Entregar a prova?"}
        </h2>
        <p className="t-ajuda">
          {modo === "estudo"
            ? "As respostas já dadas ficam — elas são registro e não voltam atrás. O que se perde é a fila montada: sair desta tela preserva a retomada, encerrar a descarta."
            : "A prova é corrigida no estado atual. Questão não respondida conta como erro: ela foi sorteada e entra na pontuação máxima."}
        </p>
        <div className="confirmar-fim__acoes">
          <button className="botao botao--secundario" type="button" onClick={aoCancelar}>
            Voltar
          </button>
          <button className="botao" type="button" autoFocus onClick={aoConfirmar}>
            {modo === "estudo" ? "Encerrar mesmo assim" : "Entregar"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
